import { ActionBarSpacer } from "$components/action-bar/action-bar-spacer";
import { BackNavigationButton } from "$components/button/back-button";
import { useChangedOrgPrices } from "$global-providers/rxdb-provider/price-review/use-changed-org-prices";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { linkToHome } from "$routes/_lib/links";
import { linkToAdminControlPanel } from "$routes/admin/links";
import { linkToClinicAdminControlPanel } from "$routes/clinic-admin/links";
import Page from "$routes/price-list/_lib/components/page-layout";
import { linkToPriceListHome } from "$routes/price-list/links";
import { gql } from "@apollo/client";
import { styled, Typography } from "@material-ui/core";
import {
  ItemPriceOwner,
  OrderDirection,
  PriceableItemOrigin,
  PriceListItemsOrderBy,
  Role,
  type SetPricesUpdate,
} from "elephant-web/gql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { Navigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useLocalStorage } from "usehooks-ts";
import type { GetPriceListQueryResult } from "./__generated__/page.gql";
import { useGetPriceListQuery } from "./__generated__/page.gql";
import { useSetPricesMutation } from "./_lib/__generated__/inline-price-cell.gql";
import { applyPercentageDelta } from "./_lib/bulk-percentage-adjust";
import EmptyPriceListState from "./_lib/empty-price-list-state";
import { PriceListActionBar } from "./_lib/price-list-action-bar";
import { PriceListFilters } from "./_lib/price-list-filters";
import { PriceListLegend } from "./_lib/price-list-legend";
import type { PriceListRow } from "./_lib/price-list-row";
import PriceListTable from "./_lib/price-list-table";
import { PriceReviewBanner } from "./_lib/price-review-banner";
import {
  type RevenueDepartmentOption,
  usePendingPriceEdits,
} from "./_lib/use-pending-price-edits";
// `useRevenueDepartmentsQuery` is consumed inside PriceListTable so this page
// doesn't re-render when that query resolves.
import { usePriceReviewState } from "./_lib/use-price-review-state";

const messages = defineMessages({
  backToControlPanel: {
    id: "priceList.navigation.backToControlPanel",
    defaultMessage: "Back to Control Panel",
  },
});

const StyledBackNavigationWrapper = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(1)}px ${theme.spacing(2)}px 0`,
}));

const StyledPage = styled(Page)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

gql`
  query revenueDepartments {
    priceListV2 {
      revenueDepartments {
        edges {
          id
          name
        }
      }
    }
  }
`;

gql`
  query getPriceList($input: PriceListItemsInput!, $options: PriceListItemsOptions) {
    priceListV2 {
      priceListItems(input: $input, options: $options) {
        pageInfo {
          pageNumber
          pageSize
          totalRows
        }
        edges {
          id
          name
          externalReference {
            origin
          }
          prices {
            id
            value
            owner
            active
            tariff {
              id
              name
            }
            revenueDepartment {
              id
              name
            }
            owner
            parentPrice {
              id
              value
              owner
              active
              tariff {
                id
                name
              }
              revenueDepartment {
                id
                name
              }
              owner
            }
          }
        }
        meta {
          linkedToOrganisation
          facilityId
        }
      }
    }
  }
`;

function mapQueryToPrices(
  input: GetPriceListQueryResult["data"],
): PriceListRow[] {
  return (
    input?.priceListV2.priceListItems.edges.flatMap((row) =>
      row.prices.map((price) => ({
        id: price.id,
        itemId: row.id,
        name: row.name,
        origin: row.externalReference.origin,
        price: price.value,
        tariff: price.tariff,
        revenueDepartment: price.revenueDepartment,
        owner: price.owner,
        active: price.active,
        parentPrice: price?.parentPrice
          ? {
              id: price.parentPrice.id,
              itemId: row.id,
              name: row.name,
              origin: row.externalReference.origin,
              price: price.parentPrice.value,
              tariff: price.parentPrice.tariff,
              revenueDepartment: price.parentPrice?.revenueDepartment,
              owner: price.parentPrice?.owner,
              active: price.parentPrice.active,
            }
          : null,
      })),
    ) ?? []
  );
}

const PageContent: FC<{
  priceListId: string;
  priceListOwner: ItemPriceOwner;
}> = ({ priceListId, priceListOwner }) => {
  const { formatMessage } = useIntl();
  const review = usePriceReviewState();
  // Authoritative view of what the organisation changed since the user last
  // reviewed, shared with the notification bell so the two can't disagree.
  const changedOrgPrices = useChangedOrgPrices();
  // The review flow is for facility admins looking at their own facility list.
  // Gate on both so an org admin can't see the banner or have `?review=true`
  // repoint the query while viewing the organisation price list.
  const eligibleForReview =
    changedOrgPrices.isFacilityAdmin &&
    priceListOwner === ItemPriceOwner.Facility;
  // Only trust the count once the scan has settled for the *current* inputs —
  // `useChangedPricesScan` keeps the previous count while `loading` flips during
  // a priceListId/ack transition, which would otherwise flash a stale banner.
  const { scan } = changedOrgPrices;
  const scanSettled =
    !scan.loading &&
    scan.scannedPriceListId === changedOrgPrices.linkedOrgPriceListId &&
    scan.scannedAcknowledgedAt === changedOrgPrices.acknowledgedAt;
  const changedCount = eligibleForReview && scanSettled ? scan.count : 0;
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useLocalStorage<number>(
    "priceList.pageSize",
    20,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 250);
  const [selectedOrigins, setSelectedOriginsState] = useState<
    PriceableItemOrigin[]
  >([]);

  const edits = usePendingPriceEdits();

  // Reset to page 0 when the debounced search term settles, so the page-reset
  // and the filtered fetch happen in a single round trip rather than firing
  // an extra unfiltered page-0 query on every keystroke.
  // biome-ignore lint/correctness/useExhaustiveDependencies: side-effect intentionally tracks debouncedSearchTerm
  useEffect(() => {
    setPageNumber(0);
  }, [debouncedSearchTerm]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: side-effect tracks review boundaries
  useEffect(() => {
    setPageNumber(0);
  }, [review.isReviewMode, review.acknowledgedAt]);

  const setSelectedOrigins = (next: PriceableItemOrigin[]) => {
    setSelectedOriginsState(next);
    setPageNumber(0);
  };

  // Review mode only applies to eligible users; an ineligible user (e.g. org
  // admin) landing on `?review=true` must not trigger any review-only query
  // behaviour, even with an acknowledgement row present.
  const reviewActive = eligibleForReview && review.isReviewMode;
  const isFilteredReview = reviewActive && !!review.acknowledgedAt;

  // While reviewing, query the linked organisation list directly rather than
  // the merged facility list: its `updatedAt` is the organisation price's own
  // timestamp, so `pricesUpdatedSince` yields exactly the prices the org
  // pushed since the user last reviewed — the same set the bell counts. The
  // merged facility list keys its window on the *effective* (facility-
  // preferring) timestamp, which mixes in the user's own edits and hides org
  // changes to overridden items.
  const reviewListId =
    isFilteredReview && changedOrgPrices.linkedOrgPriceListId
      ? changedOrgPrices.linkedOrgPriceListId
      : priceListId;

  const {
    data,
    loading,
    error,
    refetch: refetchPriceList,
  } = useGetPriceListQuery({
    variables: {
      input: {
        priceListId: reviewListId,
        searchTerm: debouncedSearchTerm || undefined,
        origins: selectedOrigins.length > 0 ? selectedOrigins : undefined,
        includeInactive: true,
        pricesUpdatedSince: isFilteredReview
          ? review.acknowledgedAt
          : undefined,
      },
      options: {
        pagination: { pageNumber, pageSize },
        orderBy: isFilteredReview
          ? PriceListItemsOrderBy.MostRecentPriceUpdate
          : undefined,
        orderDirection: isFilteredReview ? OrderDirection.Desc : undefined,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const isConnectedToOrg =
    data?.priceListV2?.priceListItems.meta?.linkedToOrganisation ?? false;

  const totalRows = data?.priceListV2.priceListItems.pageInfo.totalRows ?? 0;

  const rawRows = useMemo(() => mapQueryToPrices(data), [data]);

  const overriddenPriceIds = useMemo(
    () =>
      new Set<string>(
        rawRows
          .map((r) => r.parentPrice?.id)
          .filter((id): id is string => !!id),
      ),
    [rawRows],
  );

  const isEditable = useCallback(
    (row: PriceListRow) => row.owner === priceListOwner,
    [priceListOwner],
  );

  const hasNonEditableRows = useMemo(
    () => rawRows.some((r) => !isEditable(r)),
    [rawRows, isEditable],
  );

  // Apply pending edits onto rows so the table renders the user's
  // in-flight values instead of the saved query values.
  const displayedRows = useMemo(
    () => rawRows.map((r) => edits.effectiveRow(r)),
    [rawRows, edits.effectiveRow],
  );

  const [setPrices, { loading: submitting }] = useSetPricesMutation();

  const resetEditState = useCallback(() => {
    edits.reset();
  }, [edits.reset]);

  // Drop any pending edits when the visible window changes (pagination,
  // search, filter). Keeping them across windows would silently apply
  // changes to rows the user can no longer see; safer to discard.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tracks visible-window keys only
  useEffect(() => {
    if (edits.selectedIds.size === 0) return;
    resetEditState();
  }, [
    pageNumber,
    pageSize,
    debouncedSearchTerm,
    selectedOrigins,
    review.isReviewMode,
    review.acknowledgedAt,
  ]);

  // Block tab close / reload / external navigation while there are pending
  // changes the user hasn't saved. Browsers render their own confirmation;
  // we can't customise the copy, only opt in.
  useEffect(() => {
    if (edits.changedCount === 0) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [edits.changedCount]);

  const handleEditPrice = useCallback(
    (row: PriceListRow, valueInPence: number) => {
      edits.stageEdit(row, { price: valueInPence });
    },
    [edits.stageEdit],
  );

  const handleEditActive = useCallback(
    (row: PriceListRow, active: boolean) => {
      edits.stageEdit(row, { active });
    },
    [edits.stageEdit],
  );

  const handleEditRevenueDepartment = useCallback(
    (row: PriceListRow, revenueDepartment: RevenueDepartmentOption) => {
      edits.stageEdit(row, { revenueDepartment });
    },
    [edits.stageEdit],
  );

  const handleToggleSelected = useCallback(
    (row: PriceListRow) => {
      if (!isEditable(row)) return;
      edits.toggleSelected(row);
    },
    [edits.toggleSelected, isEditable],
  );

  const handleApplyAdjustment = useCallback(
    (percentageDelta: number) => {
      if (percentageDelta === 0) return;
      edits.applyAdjustment((row) => ({
        price: applyPercentageDelta(row.price, percentageDelta),
      }));
    },
    [edits.applyAdjustment],
  );

  const handleSave = useCallback(() => {
    const updates: SetPricesUpdate[] = [];
    for (const edit of edits.pending.values()) {
      const r = edit.row;
      const priceChanged = edit.price !== undefined && edit.price !== r.price;
      const activeChanged =
        edit.active !== undefined && edit.active !== r.active;
      const deptChanged =
        edit.revenueDepartment !== undefined &&
        edit.revenueDepartment.id !== r.revenueDepartment.id;
      if (!priceChanged && !activeChanged && !deptChanged) continue;
      updates.push({
        itemId: r.itemId,
        tariffId: r.tariff.id,
        revenueDepartmentId: deptChanged
          ? // biome-ignore lint/style/noNonNullAssertion: deptChanged guards this
            edit.revenueDepartment!.id
          : r.revenueDepartment.id,
        ...(priceChanged ? { value: edit.price } : {}),
        ...(activeChanged ? { active: edit.active } : {}),
      });
    }
    if (updates.length === 0) return;
    setPrices({
      variables: { input: { priceListId, updates } },
      onCompleted: () => {
        toast.success(
          formatMessage(
            {
              id: "priceList.editMode.saveSuccess",
              defaultMessage:
                "Saved {count, plural, one {# price} other {# prices}}.",
            },
            { count: updates.length },
          ),
        );
        resetEditState();
        refetchPriceList();
      },
      onError: (e) => {
        toast.error(
          formatMessage(
            {
              id: "priceList.editMode.saveError",
              defaultMessage: "Couldn't save prices: {message}",
            },
            { message: e.message },
          ),
        );
      },
    });
  }, [
    edits.pending,
    formatMessage,
    priceListId,
    refetchPriceList,
    resetEditState,
    setPrices,
  ]);

  const tableHandlers = useMemo(
    () => ({
      isEditable,
      isSelected: edits.isSelected,
      isChanged: edits.isChanged,
      onToggleSelected: handleToggleSelected,
      onEditPrice: handleEditPrice,
      onEditActive: handleEditActive,
      onEditRevenueDepartment: handleEditRevenueDepartment,
    }),
    [
      edits.isChanged,
      edits.isSelected,
      handleEditActive,
      handleEditPrice,
      handleEditRevenueDepartment,
      handleToggleSelected,
      isEditable,
    ],
  );

  const editModeActive = edits.selectedIds.size > 0;

  const editModeState = useMemo(
    () => ({
      active: editModeActive,
      changedCount: edits.changedCount,
      selectedCount: edits.selectedIds.size,
      onApplyAdjustment: handleApplyAdjustment,
      onSave: handleSave,
      onCancel: resetEditState,
      submitting,
    }),
    [
      edits.changedCount,
      edits.selectedIds.size,
      editModeActive,
      handleApplyAdjustment,
      handleSave,
      resetEditState,
      submitting,
    ],
  );

  if (error && !loading && !data) {
    return <Navigate to={linkToPriceListHome()} />;
  }

  return (
    <>
      {changedCount > 0 && (
        <PriceReviewBanner
          count={changedCount}
          onReview={review.isReviewMode ? undefined : review.enterReviewMode}
        />
      )}
      <PriceListFilters
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        selectedOrigins={selectedOrigins}
        onSelectedOriginsChange={setSelectedOrigins}
      />
      {hasNonEditableRows && <PriceListLegend />}
      <PriceListTable
        rows={displayedRows}
        overriddenPriceIds={overriddenPriceIds}
        handlers={tableHandlers}
        loading={loading}
        dense
        noDataMessage={<EmptyPriceListState />}
        pagination={{
          page: pageNumber,
          handlePageChange: setPageNumber,
          pageSize,
          handlePageSizeChange: (newPageSize) => {
            setPageSize(newPageSize);
            setPageNumber(0);
          },
          pageSizeOptions: [20, 50, 100],
          totalRows,
        }}
      />
      <ActionBarSpacer />
      <PriceListActionBar
        priceListId={priceListId}
        priceListOwner={priceListOwner}
        isConnectedToOrg={isConnectedToOrg}
        refetchPriceList={refetchPriceList}
        editMode={editModeState}
        reviewMode={{
          active: reviewActive,
          onAcknowledge: review.markAsReviewed,
          onGoBack: review.clearReviewMode,
          acknowledging: review.marking,
        }}
      />
    </>
  );
};

const PriceListPage = () => {
  const { id: priceListId } = useParams();
  const user = useUser();

  if (!priceListId) {
    return <Navigate to={linkToPriceListHome()} />;
  }

  const acceptableRoles = [Role.Admin, Role.Clinic];
  if (!acceptableRoles.includes(user.role)) {
    return <Navigate to={linkToHome()} />;
  }

  const priceListOwner =
    user.role === Role.Clinic
      ? ItemPriceOwner.Facility
      : ItemPriceOwner.Organisation;

  const controlPanelLink =
    user.role === Role.Clinic
      ? linkToClinicAdminControlPanel()
      : linkToAdminControlPanel();

  return (
    <>
      <StyledBackNavigationWrapper>
        <BackNavigationButton
          to={controlPanelLink}
          label={messages.backToControlPanel}
        />
      </StyledBackNavigationWrapper>
      <StyledPage>
        <Typography variant="h2">
          {priceListOwner === ItemPriceOwner.Organisation ? (
            <FormattedMessage
              id="pricelist.header.organisation.title"
              defaultMessage="Organisation Price List"
            />
          ) : (
            <FormattedMessage
              id="pricelist.header.facility.title"
              defaultMessage="Facility Price List"
            />
          )}
        </Typography>
        <PageContent
          priceListId={priceListId}
          priceListOwner={priceListOwner}
        />
      </StyledPage>
    </>
  );
};

export default PriceListPage;
